import * as FullStory from "@fullstory/browser";
import { AnalyticsEvent, LoanOfficerDetail, StepResponse } from "pages/application/applicationService";

let isFullStoryShutdown = false;

const isFullStoryEnabled = () => window.env.ENABLE_FULL_STORY && !isFullStoryShutdown;

/** Module-level set — shared across all E-Sign tracking code for the session lifetime.
 *  Prevents double-firing when multiple components/hooks attempt to track the same event.
 *  Survives component remounts and ensures the event fires exactly once per package per session. */
export const sessionESignEventKeys = new Set<string>();

const initFullStory = () => {
  isFullStoryShutdown = false;
  if (!window.env.ENABLE_FULL_STORY) return;
  FullStory.init({ orgId: window.env.FULLSTORY_ORG_ID });
};

/**
 * Immediately disables Full Story data capture
 */
export const shutdownFullStory = () => {
  isFullStoryShutdown = true;
  FullStory.shutdown();
};

/**
 * @param stepCode internal code for the step
 * @param stepLabel String name for StepCode
 * @param skipped true if user skip
 */
export function trackFSStepCompleted(stepCode: string, stepLabel: string, skipped: boolean) {
  if (!isFullStoryEnabled()) return;
  FullStory.event("Step completed", {
    StepCode: stepCode,
    StepName: stepLabel,
    Skipped: skipped ? "Yes" : "No",
  });
}

export function trackFSApplicationStarted(props: {
  leadProvider?: string;
  loanType?: string;
  pathSelected?: string;
  leadCampaign?: string;
  applicationStartedDate: string;
  loanOfficerAssigned?: string;
  borrowerType?: string;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, loanType, pathSelected, leadCampaign, loanOfficerAssigned, applicationStartedDate, borrowerType } = props;

  FullStory.event("Application Started", {
    StartedDate: applicationStartedDate,
    LeadProvider: leadProvider ?? "Unknown",
    LeadCampaign: leadCampaign ?? "Unknown",
    LoanType: loanType ?? "Unknown",
    PathSelected: pathSelected ?? "Unknown",
    LoanOfficerAssigned: loanOfficerAssigned ?? "Not Assigned",
    Environment: window.env.ENVIRONMENT ?? "Unknown",
    borrowerType: borrowerType,
  });
}

const trackFSSummaryEvent = (eventName: string, eventPayload: Record<string, unknown>) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, { ...eventPayload });
};

const trackFSSummaryEditEvent = (stepCode: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Summary Page Edited", {
    SummaryPage: stepCode,
  });
};

export const trackFSInitAppSubmittedEvent = () => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Initial App Submitted", {
    DateSubmitted: new Date().toISOString(),
  });
};

const trackFSOptedVoaEvent = (orderDate: Date) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Opted Voa", { VoaOrderDate: orderDate });
};

const trackFSOptedVoiEvent = (orderDate: Date) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Opted VOI", { VoiOrderDate: orderDate });
};

const trackFSLOAssignedEvent = (loanOfficerDetails: LoanOfficerDetail, stepData: StepResponse) => {
  if (!isFullStoryEnabled()) return;
  FullStory.event("Loan Officer Assigned", {
    LoanOfficerName: loanOfficerDetails.fullName,
    LoanOfficerNMLS: loanOfficerDetails.nmlsLicenseNumber,
    StepCode: stepData.stepCodeText,
    Section: stepData.section,
  });
};

export function trackFSVisitedRegistrationPage(props:{
  leadProvider?: string;
  loanType?: string;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, loanType } = props;

  FullStory.event("Visited Registration Page", {
    LeadProvider: leadProvider ?? "Unknown",
    LoanType: loanType ?? "Unknown",
  });
}

const setFSNavigatedToInsuranceCustomEvent = (nhDealApplicationId: string, redirectionUrl: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("NavigatedToInsurance", {
    nhDealApplicationId,
    redirectionUrl,
  });
};

const setFSUserProperties = (email?: string, lastName?: string) => {
  if (isFullStoryEnabled() && email) {
    FullStory.setUserVars({ email });
  }
  if (isFullStoryEnabled() && lastName) {
    FullStory.setUserVars({ lastName });
  }
};

interface AnalyticsSection {
  section: string;
  eventType: AnalyticsEvent;
  displayName: string;
}

export const analyticsSections: AnalyticsSection[] = [
  { section: "Lead", eventType: "FSLeadCompleted", displayName: "Lead Completed" },
  { section: "Profile", eventType: "FSProfileCompleted", displayName: "Profile Completed" },
  { section: "Property", eventType: "FSPropertyCompleted", displayName: "Property Completed" },
  { section: "Credit", eventType: "FSCreditCompleted", displayName: "Credit Completed" },
  { section: "Finances", eventType: "FSFinanceCompleted", displayName: "Finance Completed" },
  { section: "Acknowledgements", eventType: "FSDeclarationsCompleted", displayName: "Declarations Completed" },
];

export const trackFSNpsDisplayed = (props: {
  count: number;
  loanPurpose?: string;
  salesChannel?: string;
}) => {
  if (!isFullStoryEnabled()) return;

  const { count, loanPurpose, salesChannel } = props;

  FullStory.event("NPS Displayed", {
    Count: count,
    LoanPurpose: loanPurpose ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
  });
};

export const trackFSNpsCompleted = (props: { isComplete: boolean }) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("NPS Completed", {
    NPSComplete: props.isComplete,
  });
};

export const trackFSNpsClosed = (props: {
  loanPurpose?: string;
  salesChannel?: string;
  npsScore?: number | null;
  npsFeedback?: string | null;
}) => {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, npsScore, npsFeedback } = props;

  FullStory.event("NPS Closed", {
    LoanPurpose: loanPurpose ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
    NPSScore: typeof npsScore === "number" ? npsScore : null,
    NPSFeedback: npsFeedback ?? "",
  });
};

export const trackFSServicingCtaClicked = (userId?: string, nhDealApplicationId?: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Servicing CTA Clicked", {
    UserId: userId ?? "Unknown",
    NhDealApplicationId: nhDealApplicationId ?? "Unknown",
  });
};

interface ExitIntentBaseProps {
  loanPurpose?: string;
  salesChannel?: string;
  stepCode?: string;
  stepCodeName?: string;
}

interface ExitIntentWithAnswersProps extends ExitIntentBaseProps {
  question1?: string;
  question2?: string;
}

export const trackFSExitIntentDisplayed = (props: ExitIntentBaseProps) => {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, stepCode, stepCodeName } = props;

  FullStory.event("Exit Intent Displayed", {
    "Loan Purpose": loanPurpose || "Unknown",
    "Sales Channel": salesChannel || "Unknown",
    Stepcode: stepCode || "Unknown",
    "Stepcode Name": stepCodeName || "Unknown",
  });
};

function trackFSExitIntentWithAnswers(
  eventName: "Exit Intent Completed" | "Exit Intent Cancelled",
  props: ExitIntentWithAnswersProps
) {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, stepCode, stepCodeName, question1, question2 } = props;

  FullStory.event(eventName, {
    "Loan Purpose": loanPurpose || "Unknown",
    "Sales Channel": salesChannel || "Unknown",
    Stepcode: stepCode || "Unknown",
    "Stepcode Name": stepCodeName || "Unknown",
    "Question 1": question1 || "",
    "Question 2": question2 || "",
  });
}

export const trackFSExitIntentCompleted = (props: ExitIntentWithAnswersProps) =>
  trackFSExitIntentWithAnswers("Exit Intent Completed", props);

export const trackFSExitIntentCancelled = (props: ExitIntentWithAnswersProps) =>
  trackFSExitIntentWithAnswers("Exit Intent Cancelled", props);
export const trackFSDocumentsUploaded = (props: {
  username: string;
  timestamp: string;
  prospectId: string;
  dealId: string | null;
  losLoanNumber: string | null;
  losId: string | null;
  numberOfDocumentsUploaded: number;
  loanMilestone: string;
}) => {
  if (!isFullStoryEnabled()) return;

  const { username, timestamp, prospectId, dealId, losLoanNumber, losId, numberOfDocumentsUploaded, loanMilestone } = props;

  FullStory.event("Documents Uploaded", {
    Username: username,
    Timestamp: timestamp,
    ProspectID: prospectId,
    DealID: dealId,
    LOSLoanNumber: losLoanNumber,
    LOSID: losId,
    NumberOfDocumentsUploaded: numberOfDocumentsUploaded,
    LoanMilestone: loanMilestone,
  });
};

type ESignFullStoryProps = {
  username?: string;
  prospectId?: string;
  dealId?: string | null;
  timestamp: string;
  eventName?: string;
  losLoanNumber?: string | null;
  losGuid?: string | null;
  loanMilestone?: string;
};

const E_SIGN_EVENT_NAMES = {
  created: "E-Sign Created",
  started: "E-Sign Started",
  completed: "E-Sign Completed",
} as const;

type ESignEventName = (typeof E_SIGN_EVENT_NAMES)[keyof typeof E_SIGN_EVENT_NAMES];

const trackFSESignEvent = (eventName: ESignEventName, props: ESignFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, {
    Username: props.username ?? "Unknown",
    ProspectID: props.prospectId ?? "Unknown",
    DealID: props.dealId ?? "Unknown",
    Timestamp: props.timestamp,
    EventName: props.eventName ?? eventName,
    LOSLoanNumber: props.losLoanNumber ?? "Unknown",
    LOSGUID: props.losGuid ?? "Unknown",
    LoanMilestone: props.loanMilestone ?? "Unknown",
  });
};

export const trackFSESignCreated = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.created, props);

export const trackFSESignStarted = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.started, props);

export const trackFSESignCompleted = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.completed, props);

export {
  initFullStory,
  setFSUserProperties,
  setFSNavigatedToInsuranceCustomEvent,
  trackFSSummaryEvent,
  trackFSSummaryEditEvent,
  trackFSOptedVoaEvent,
  trackFSLOAssignedEvent,
  trackFSOptedVoiEvent,
};
